import { Plus, X, Crosshair, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useOptionSetsByIds } from '@/hooks/useOptionSets'
import { formulaToHuman } from '@/lib/formula'
import { FormFieldRefSelect, type RefFormGroup } from './FormFieldRefSelect'
import type { FormField, VisibilityRule } from './types'
import type { VisibilityCondition, VisibilityGroup, VisibilityOperator } from '@/types/cgt'

/** Which condition row a canvas click-to-pick is currently armed for. */
export interface VisibilityPickTarget {
  groupIdx: number
  condIdx: number
}

interface Props {
  rule: VisibilityRule | undefined
  onChange: (rule: VisibilityRule | undefined) => void
  /** Eligible source fields — pre-filtered by the caller (value-bearing, no self, no cycles). */
  candidateFields: FormField[]
  /**
   * The same sources grouped by form for the hierarchical picker: this form
   * first, then earlier forms per the toolbar template context.
   */
  formGroups: RefFormGroup[]
  /** 'field' | 'section' — used in copy. */
  targetLabel: string
  /** Offer the "make required when shown" toggle (fields only). */
  allowRequire: boolean
  /** Condition row currently armed for canvas click-to-pick (null = none). */
  pickArmed: VisibilityPickTarget | null
  /** Arm/disarm canvas click-to-pick for a condition row. */
  onArmPick: (target: VisibilityPickTarget | null) => void
}

const OPERATOR_LABELS: Record<VisibilityOperator, string> = {
  equals: '=',
  notEquals: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
}

/** Comparison operators only make sense for orderable sources. */
const isComparableType = (t: FormField['type']) =>
  t === 'number' || t === 'date' || t === 'calculated'

/** Substring operators fit free-text-ish sources (and dropdown labels). */
const isTextLikeType = (t: FormField['type']) =>
  t === 'text' || t === 'textarea' || t === 'select' || t === 'signature'

const TEXT_OPERATORS: VisibilityOperator[] = ['contains', 'startsWith', 'endsWith']

export const operatorsFor = (source: FormField | undefined): VisibilityOperator[] => {
  if (source && isComparableType(source.type))
    return ['equals', 'notEquals', 'gt', 'gte', 'lt', 'lte']
  if (source && isTextLikeType(source.type))
    return ['equals', 'notEquals', ...TEXT_OPERATORS]
  return ['equals', 'notEquals']
}

const emptyCondition = (): VisibilityCondition => ({
  sourceFieldId: '',
  operator: 'equals',
  value: '',
})

const defaultRule = (): VisibilityRule => ({
  logic: 'and',
  groups: [{ logic: 'and', conditions: [emptyCondition()] }],
  action: 'show',
})

/** Tiny AND/OR connector toggle rendered between rows/groups. */
function ConnectorToggle({
  value,
  onChange,
}: {
  value: 'and' | 'or'
  onChange: (v: 'and' | 'or') => void
}) {
  return (
    <div className='flex justify-center'>
      <div className='inline-flex overflow-hidden rounded-md border text-[10px] font-semibold uppercase'>
        {(['and', 'or'] as const).map(v => (
          <button
            key={v}
            type='button'
            className={cn(
              'px-2 py-0.5 transition-colors',
              value === v
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:text-foreground',
            )}
            onClick={() => onChange(v)}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Condition-expression builder for value-driven visibility rules. Renders the
 * two-level tree: bracketed groups joined by the rule connector, condition rows
 * inside each group joined by the group connector. Source fields come from a
 * dropdown or canvas click-to-pick (the crosshair arms pick mode; the page
 * routes the next canvas click back into the armed row).
 */
export function VisibilityBuilder({
  rule,
  onChange,
  candidateFields,
  formGroups,
  targetLabel,
  allowRequire,
  pickArmed,
  onArmPick,
}: Props) {
  const sourceById = new Map(candidateFields.map(f => [f.id, f]))
  // Label map for a formula condition's human-readable preview — every field
  // offered by the picker, across every form group (own form + earlier forms).
  const labelById = Object.fromEntries(
    formGroups.flatMap(g => g.fields.map(f => [f.refKey, f.label])),
  )

  // Resolve Option Set options for any referenced dropdown source so the value
  // picker can offer its choices. Unconditional hook call; [] → no request.
  const optionSetIds = rule
    ? [
        ...new Set(
          rule.groups
            .flatMap(g => g.conditions)
            .map(c => sourceById.get(c.sourceFieldId))
            .filter(f => f?.optionsSource === 'optionSet' && f.optionSetId)
            .map(f => f!.optionSetId!),
        ),
      ]
    : []
  const optionSets = useOptionSetsByIds(optionSetIds)

  if (!rule) {
    return (
      <div className='space-y-1.5'>
        <Label>Conditions</Label>
        <Button
          variant='outline'
          size='sm'
          className='w-full'
          onClick={() => onChange(defaultRule())}
          disabled={candidateFields.length === 0}
        >
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add condition
        </Button>
        <p className='text-xs text-muted-foreground'>
          {candidateFields.length === 0
            ? 'No other value-bearing fields to depend on.'
            : `Show or hide this ${targetLabel} based on other fields' values.`}
        </p>
      </div>
    )
  }

  const update = (patch: Partial<VisibilityRule>) => onChange({ ...rule, ...patch })

  const updateGroup = (gi: number, patch: Partial<VisibilityGroup>) =>
    update({ groups: rule.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) })

  const updateCondition = (gi: number, ci: number, patch: Partial<VisibilityCondition>) =>
    updateGroup(gi, {
      conditions: rule.groups[gi].conditions.map((c, i) =>
        i === ci ? { ...c, ...patch } : c,
      ),
    })

  const setSource = (gi: number, ci: number, sourceFieldId: string) => {
    // Picking from the dropdown supersedes an armed canvas pick — leaving it
    // armed would let the next canvas click silently overwrite this choice.
    onArmPick(null)
    const source = sourceById.get(sourceFieldId)
    const current = rule.groups[gi].conditions[ci]
    const validOps = operatorsFor(source)
    updateCondition(gi, ci, {
      sourceFieldId,
      // Reset an operator the new source can't support; clear the stale value.
      operator: validOps.includes(current.operator) ? current.operator : 'equals',
      value: '',
    })
  }

  const removeCondition = (gi: number, ci: number) => {
    onArmPick(null)
    const groups = rule.groups
      .map((g, i) =>
        i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g,
      )
      .filter(g => g.conditions.length > 0)
    if (groups.length === 0) onChange(undefined)
    else update({ groups })
  }

  // A condition row is either the structured field/operator/value form or a
  // raw Boolean formula — the escape hatch for conditions the structured form
  // can't express (e.g. date arithmetic). Toggling clears the other shape's
  // fields so a stale value never lingers underneath.
  const toFormulaMode = (gi: number, ci: number) => {
    onArmPick(null)
    updateCondition(gi, ci, { formula: '', sourceFieldId: '', value: '' })
  }
  const toFieldMode = (gi: number, ci: number) => {
    updateCondition(gi, ci, { formula: undefined, sourceFieldId: '', operator: 'equals', value: '' })
  }

  const valueInput = (gi: number, ci: number, cond: VisibilityCondition) => {
    const source = sourceById.get(cond.sourceFieldId)
    const cls = 'h-8 text-xs'
    // Substring operators match fragments — free text even for dropdown sources.
    if (TEXT_OPERATORS.includes(cond.operator)) {
      return (
        <Input
          className={cls}
          value={String(cond.value)}
          placeholder='Text…'
          onChange={e => updateCondition(gi, ci, { value: e.target.value })}
        />
      )
    }
    if (source?.type === 'select') {
      const options =
        source.optionsSource === 'optionSet' && source.optionSetId
          ? (optionSets.find(s => s.id === source.optionSetId)?.options ?? [])
          : source.options
      // Cross-form dropdown sources carry no option list — free-text compare.
      if (options.length === 0) {
        return (
          <Input
            className={cls}
            value={String(cond.value)}
            placeholder='Value…'
            onChange={e => updateCondition(gi, ci, { value: e.target.value })}
          />
        )
      }
      return (
        <Select
          value={String(cond.value)}
          onValueChange={v => updateCondition(gi, ci, { value: v })}
        >
          <SelectTrigger className={cls}>
            <SelectValue placeholder='Value…' />
          </SelectTrigger>
          <SelectContent>
            {options.map(o => (
              <SelectItem key={o.id} value={o.label} className='text-xs'>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    if (source?.type === 'checkbox' || source?.type === 'switch') {
      return (
        <Select
          value={String(cond.value)}
          onValueChange={v => updateCondition(gi, ci, { value: v })}
        >
          <SelectTrigger className={cls}>
            <SelectValue placeholder='Value…' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='true' className='text-xs'>Checked</SelectItem>
            <SelectItem value='false' className='text-xs'>Unchecked</SelectItem>
          </SelectContent>
        </Select>
      )
    }
    if (source?.type === 'number' || source?.type === 'calculated') {
      return (
        <Input
          type='number'
          className={cls}
          value={String(cond.value)}
          placeholder='Value…'
          onChange={e => {
            const raw = e.target.value
            updateCondition(gi, ci, { value: raw === '' ? '' : Number(raw) })
          }}
        />
      )
    }
    if (source?.type === 'date') {
      return (
        <Input
          type='date'
          className={cls}
          value={String(cond.value)}
          onChange={e => updateCondition(gi, ci, { value: e.target.value })}
        />
      )
    }
    return (
      <Input
        className={cls}
        value={String(cond.value)}
        placeholder='Value…'
        onChange={e => updateCondition(gi, ci, { value: e.target.value })}
      />
    )
  }

  return (
    <div className='space-y-2'>
      <Label>Conditions</Label>

      {/* Action — what the expression does to this target */}
      <div className='flex items-center gap-2'>
        <Select
          value={rule.action}
          onValueChange={v =>
            update({
              action: v as VisibilityRule['action'],
              // "Required when shown" only pairs with show rules.
              require: v === 'show' ? rule.require : undefined,
            })
          }
        >
          <SelectTrigger className='h-8 w-24 text-xs'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='show' className='text-xs'>Show</SelectItem>
            <SelectItem value='hide' className='text-xs'>Hide</SelectItem>
          </SelectContent>
        </Select>
        <span className='text-xs text-muted-foreground'>this {targetLabel} when…</span>
      </div>

      {/* Groups — each box is one pair of brackets */}
      {rule.groups.map((group, gi) => (
        <div key={gi} className='space-y-2'>
          {gi > 0 && (
            <ConnectorToggle value={rule.logic} onChange={v => update({ logic: v })} />
          )}
          <div
            className={cn(
              'space-y-2 rounded-md border p-2',
              rule.groups.length > 1 && 'border-primary/30 bg-primary/[0.03]',
            )}
          >
            {group.conditions.map((cond, ci) => {
              const source = sourceById.get(cond.sourceFieldId)
              const armed = pickArmed?.groupIdx === gi && pickArmed?.condIdx === ci
              const formulaMode = cond.formula !== undefined
              return (
                <div key={ci} className='space-y-1'>
                  {ci > 0 && (
                    <ConnectorToggle
                      value={group.logic}
                      onChange={v => updateGroup(gi, { logic: v })}
                    />
                  )}
                  {formulaMode ? (
                    <div className='space-y-1 rounded-md border bg-muted/20 p-1.5'>
                      <div className='flex items-center gap-1'>
                        <Textarea
                          value={cond.formula ?? ''}
                          onChange={e => updateCondition(gi, ci, { formula: e.target.value })}
                          placeholder='e.g. TODAY() &lt;= {expiration}'
                          className='min-h-14 flex-1 font-mono text-xs'
                        />
                        <div className='flex shrink-0 flex-col gap-1'>
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-8 w-8 text-muted-foreground'
                            title='Switch back to field/operator/value'
                            onClick={() => toFieldMode(gi, ci)}
                          >
                            <Wand2 className='h-3.5 w-3.5' />
                          </Button>
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
                            aria-label='Remove condition'
                            onClick={() => removeCondition(gi, ci)}
                          >
                            <X className='h-3.5 w-3.5' />
                          </Button>
                        </div>
                      </div>
                      <FormFieldRefSelect
                        size='sm'
                        groups={formGroups}
                        value=''
                        suggestFor={{ formula: cond.formula ?? '' }}
                        onChange={v =>
                          updateCondition(gi, ci, {
                            formula: `${cond.formula ?? ''}${cond.formula ? ' ' : ''}{${v}}`,
                          })
                        }
                        formPlaceholder='Pick a form…'
                        fieldPlaceholder='Insert a field…'
                      />
                      {cond.formula?.trim() && (
                        <p className='text-[11px] text-muted-foreground'>
                          <span className='font-medium'>Reads as:</span>{' '}
                          {formulaToHuman(cond.formula, labelById)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className='flex items-start gap-1'>
                        <FormFieldRefSelect
                          size='sm'
                          groups={formGroups}
                          value={cond.sourceFieldId}
                          // The group's other conditions are what this one goes with.
                          suggestFor={{
                            formula: group.conditions
                              .filter((c, i) => i !== ci && c.sourceFieldId)
                              .map(c => `{${c.sourceFieldId}}`)
                              .join(' '),
                          }}
                          onChange={v => setSource(gi, ci, v)}
                          formPlaceholder='Pick a form…'
                          fieldPlaceholder='Pick a field…'
                        />
                        <Button
                          size='icon'
                          variant={armed ? 'default' : 'ghost'}
                          className='h-8 w-8 shrink-0'
                          title={
                            armed
                              ? 'Click a field on the canvas — or click again to cancel'
                              : 'Pick the source by clicking a field on the canvas'
                          }
                          aria-pressed={armed}
                          onClick={() => onArmPick(armed ? null : { groupIdx: gi, condIdx: ci })}
                        >
                          <Crosshair className='h-3.5 w-3.5' />
                        </Button>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='h-8 w-8 shrink-0 text-muted-foreground'
                          title='Switch to a formula instead'
                          onClick={() => toFormulaMode(gi, ci)}
                        >
                          <Wand2 className='h-3.5 w-3.5' />
                        </Button>
                      </div>
                      <div className='flex items-center gap-1'>
                        <Select
                          value={cond.operator}
                          onValueChange={v =>
                            updateCondition(gi, ci, { operator: v as VisibilityOperator })
                          }
                        >
                          <SelectTrigger className='h-8 w-fit min-w-16 shrink-0 gap-1 text-xs'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {operatorsFor(source).map(op => (
                              <SelectItem key={op} value={op} className='text-xs'>
                                {OPERATOR_LABELS[op]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className='flex-1'>{valueInput(gi, ci, cond)}</div>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
                          aria-label='Remove condition'
                          onClick={() => removeCondition(gi, ci)}
                        >
                          <X className='h-3.5 w-3.5' />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
            <Button
              variant='ghost'
              size='sm'
              className='h-7 w-full text-xs text-muted-foreground'
              onClick={() => updateGroup(gi, { conditions: [...group.conditions, emptyCondition()] })}
            >
              <Plus className='mr-1 h-3 w-3' />
              Condition
            </Button>
          </div>
        </div>
      ))}

      <Button
        variant='outline'
        size='sm'
        className='h-7 w-full text-xs'
        onClick={() =>
          update({ groups: [...rule.groups, { logic: 'and', conditions: [emptyCondition()] }] })
        }
      >
        <Plus className='mr-1 h-3 w-3' />
        Group (brackets)
      </Button>

      {allowRequire && rule.action === 'show' && (
        <div className='flex items-center justify-between'>
          <Label className='font-normal cursor-pointer text-xs' htmlFor='visibility-require'>
            Make required when shown
          </Label>
          <Switch
            id='visibility-require'
            checked={!!rule.require}
            onCheckedChange={v => update({ require: v || undefined })}
          />
        </div>
      )}

      <p className='text-xs text-muted-foreground'>
        {rule.action === 'show'
          ? `Hidden until the conditions are met, then this ${targetLabel} appears.`
          : `Visible until the conditions are met, then this ${targetLabel} hides.`}{' '}
        Values already entered are kept while hidden.
      </p>
    </div>
  )
}
